import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/css/Signin.css";
import axiosInstance from "../components/api/axiosInstance";

const CTX_PATH = process.env.REACT_APP_API_CTX_PATH || "/cld-office";

function PortalIcon() {
  return (
    <span className="signin-badge">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18" />
        <path d="M5 21V7l7-4 7 4v14" />
        <path d="M9 21v-6h6v6" />
        <path d="M9 9h.01M15 9h.01M9 12h.01M15 12h.01" />
      </svg>
    </span>
  );
}

function Signin({ onLogin }) {
  const navigate = useNavigate();

  // "client" (default portal login) or "staff" (existing CRM login)
  const [view, setView] = useState("client");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Customer auth sub-mode: "password" or "otp"
  const [customerAuthMode, setCustomerAuthMode] = useState("password");
  const [otpStep, setOtpStep] = useState("request"); // "request" | "verify"
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [info, setInfo] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const resetMessages = () => {
    setError("");
    setInfo("");
  };

  const switchView = (next) => {
    setView(next);
    setCustomerAuthMode("password");
    setOtpStep("request");
    setPassword("");
    setOtp("");
    setMobile("");
    resetMessages();
  };

  const persistClientSession = (data) => {
    const { access_token, refresh_token, client } = data;
    if (access_token) localStorage.setItem("access_token", access_token);
    if (refresh_token) localStorage.setItem("refresh_token", refresh_token);
    if (client) localStorage.setItem("client", JSON.stringify(client));
    localStorage.setItem("account_type", "client");
    localStorage.setItem("is_authenticated", "true");
    localStorage.removeItem("user");
    localStorage.removeItem("user_role_permissions");
    window.location.href = `${CTX_PATH}/portal`;
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("All fields are required");
      return;
    }
    resetMessages();
    setLoading(true);
    try {
      const response = await axiosInstance.post("/signin", {
        company_email: email.trim(),
        password,
      });
      const data = response.data;
      if (data && data.status === "success" && data.data) {
        const { access_token, refresh_token, user } = data.data;
        if (access_token) localStorage.setItem("access_token", access_token);
        if (refresh_token) localStorage.setItem("refresh_token", refresh_token);
        if (user) localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("account_type", "staff");
        localStorage.setItem("is_authenticated", "true");
        localStorage.removeItem("client");

        const primaryRole = Array.isArray(user?.roles) && user.roles.length > 0 ? user.roles[0] : null;
        const roleId =
          primaryRole?.role_id ??
          primaryRole?.Role_Id ??
          primaryRole?.Designation_Id ??
          primaryRole?.designation_id ??
          primaryRole?.id ??
          user?.role_id ??
          user?.Role_Id ??
          user?.roleId ??
          null;
        let permsToStore = null;
        try {
          const cacheRaw = localStorage.getItem("role_permissions_cache");
          if (cacheRaw && roleId != null) {
            const cache = JSON.parse(cacheRaw);
            const cachedForRole = cache?.[String(roleId)];
            if (Array.isArray(cachedForRole) && cachedForRole.length > 0) {
              permsToStore = cachedForRole.map((p) => ({
                Module_Name: p.module ?? p.Module_Name ?? p.module_name ?? "",
                Permission_Name: p.name ?? p.Permission_Name ?? p.permission_name ?? "",
                Permission_Id: p.id ?? p.Permission_Id ?? p.permission_id,
                module_name: p.module,
                permission_name: p.name,
                permission_id: p.id,
              }));
            }
          }
        } catch (_) {}
        if (!permsToStore) {
          permsToStore =
            Array.isArray(primaryRole?.role_permissions) ? primaryRole.role_permissions :
            Array.isArray(user?.assigned_permissions) ? user.assigned_permissions :
            Array.isArray(user?.role_permissions) ? user.role_permissions :
            Array.isArray(primaryRole?.permissions) ? primaryRole.permissions : [];
        }
        if (Array.isArray(permsToStore) && permsToStore.length > 0) {
          localStorage.setItem("user_role_permissions", JSON.stringify(permsToStore));
        } else {
          localStorage.removeItem("user_role_permissions");
        }

        if (onLogin) onLogin();
      } else {
        setError(data?.message || "Login failed. Please try again.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Login failed. Please check your credentials and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Email and password are required");
      return;
    }
    resetMessages();
    setLoading(true);
    try {
      const response = await axiosInstance.post("/client/signin", {
        identifier: email.trim(),
        password,
      });
      const data = response.data;
      if (data && data.status === "success" && data.data) {
        persistClientSession(data.data);
      } else {
        setError(data?.message || "Login failed. Please try again.");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (mobile.replace(/\D/g, "").length < 10) {
      setError("Enter a valid mobile number");
      return;
    }
    resetMessages();
    setLoading(true);
    try {
      const response = await axiosInstance.post("/client/request-otp", {
        mobile: mobile.trim(),
      });
      const data = response.data;
      if (data && data.status === "success") {
        setInfo("If an account exists, an OTP has been sent to this mobile number.");
        setOtpStep("verify");
      } else {
        setError(data?.message || "Could not send code. Please try again.");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not send code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) {
      setError("Enter the code sent to your email");
      return;
    }
    resetMessages();
    setLoading(true);
    try {
      const response = await axiosInstance.post("/client/verify-otp", {
        mobile: mobile.trim(),
        otp: otp.trim(),
      });
      const data = response.data;
      if (data && data.status === "success" && data.data) {
        persistClientSession(data.data);
      } else {
        setError(data?.message || "Invalid or expired code.");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  const renderCustomer = () => (
    <>
      {error && <p className="error">{error}</p>}
      {info && <p className="signin-info">{info}</p>}

      {customerAuthMode === "password" && (
        <form onSubmit={handleCustomerPasswordSubmit}>
          <div className="input-group">
            <label>Email/Mobile Number</label>
            <input
              type="text"
              placeholder=""
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="input-group">
            <label>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <button type="submit" className="login-button" disabled={loading}>
            {loading ? "Signing in..." : "Sign in"}
          </button>
          <div className="signin-inline-link">
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setCustomerAuthMode("otp");
                setOtpStep("request");
                resetMessages();
              }}
            >
              Login with mobile OTP instead
            </button>
          </div>
        </form>
      )}

      {customerAuthMode === "otp" && otpStep === "request" && (
        <form onSubmit={handleRequestOtp}>
          <div className="input-group">
            <label>Mobile number</label>
            <input
              type="tel"
              inputMode="numeric"
              placeholder="+91 98765 43210"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="login-button" disabled={loading}>
            {loading ? "Sending..." : "Send OTP to mobile"}
          </button>
          <div className="signin-inline-link">
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setCustomerAuthMode("password");
                resetMessages();
              }}
            >
              Use password instead
            </button>
          </div>
        </form>
      )}

      {customerAuthMode === "otp" && otpStep === "verify" && (
        <form onSubmit={handleVerifyOtp}>
          <div className="input-group">
            <label>Enter OTP</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="6-digit code"
              required
            />
          </div>
          <button type="submit" className="login-button" disabled={loading}>
            {loading ? "Verifying..." : "Verify & sign in"}
          </button>
          <div className="signin-inline-link">
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setOtpStep("request");
                setOtp("");
                resetMessages();
              }}
            >
              Resend / change number
            </button>
          </div>
        </form>
      )}
    </>
  );

  const renderStaff = () => (
    <>
      {error && <p className="error">{error}</p>}
      <form onSubmit={handleStaffSubmit}>
        <div className="input-group">
          <label>Company Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="input-group">
          <label>Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <div className="forgot-password-link">
          <button type="button" className="forgot-password-btn" onClick={() => navigate("/forgot-password")}>
            Forgot Password?
          </button>
        </div>
        <button type="submit" className="login-button" disabled={loading}>
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </>
  );

  return (
    <div className="signin-container">
      <div className="signin-card">
        <PortalIcon />
        <h2 className="signin-title">{view === "client" ? "Cloud Connect" : "Cloud Office"}</h2>
        <p className="signin-subtitle">
          {view === "client" ? "Sign in with email, mobile, or OTP" : "Sign in using company email and password"}
        </p>

        <div className="signin-toggle">
          <button
            type="button"
            className={`signin-toggle-btn ${view === "staff" ? "active" : ""}`}
            onClick={() => switchView("staff")}
          >
            Staff
          </button>
          <button
            type="button"
            className={`signin-toggle-btn ${view === "client" ? "active" : ""}`}
            onClick={() => switchView("client")}
          >
            Client
          </button>
        </div>

        {view === "client" ? renderCustomer() : renderStaff()}
      </div>
    </div>
  );
}

export default Signin;
